/**
 * Vendored from `tamoomeh-service/packages/contracts/src/customer-constants.ts`.
 *
 * The Tamoomeh Core API validates a customer request against the same catalog this
 * widget renders, so the two must agree. That copy is canonical; this one is a
 * byte-for-byte copy with only this header added, and `npm run check:contracts`
 * fails when they diverge. Edit Tamoomeh's copy and re-copy, never this file.
 */

/**
 * Customer-surface constants.
 *
 * Deliberately dependency-free — no zod, no node built-ins, no other module in
 * this package. This file is what a *browser* imports: the customer client is
 * bundled into one self-contained script for Tamoomeh and Porabzar, and pulling
 * `@tamoomeh/contracts`'s barrel in would drag zod and `node:crypto` into that
 * bundle. Keeping the runtime values here means the browser graph stays empty.
 *
 * `customer-auth.ts` re-exports everything in this file, so importing from the
 * package root keeps working for server code.
 */

/** Cookie holding the same-site customer session token (opaque, httpOnly). */
export const CUSTOMER_SESSION_COOKIE = 'tamoomeh_customer_session';
/** Cookie holding the signed double-submit CSRF token for cookie-authenticated writes. */
export const CUSTOMER_CSRF_COOKIE = 'tamoomeh_customer_csrf';
/**
 * Opaque httpOnly cookie for a Google identity that still needs phone OTP.
 * Never read from JavaScript; the session probe reports `needsPhoneVerification`.
 */
export const GOOGLE_PENDING_COOKIE = 'tamoomeh_oauth_pending';
/** Header a cookie-authenticated client must echo the CSRF token in. */
export const CUSTOMER_CSRF_HEADER = 'x-tamoomeh-csrf';
/** Header the SSO relying party uses to prove which origin it is calling from. */
export const SSO_ORIGIN_HEADER = 'x-tamoomeh-origin';
/** Header a relying party's same-origin bridge sets on its own requests. */
export const SSO_BRIDGE_HEADER = 'x-tamoomeh-bridge';

/** Path prefix every customer-facing JSON endpoint lives under. */
export const CUSTOMER_API_PREFIX = '/customer';

/** How long an issued authorization code stays exchangeable. */
export const SSO_CODE_TTL_SECONDS = 60;
/** How long a customer browser session stays valid without re-authentication. */
export const CUSTOMER_SESSION_TTL_SECONDS = 30 * 24 * 60 * 60;
/** Minimum acceptable PKCE verifier length (RFC 7636). */
export const SSO_CODE_VERIFIER_MIN_LENGTH = 43;

/** Grant types the token endpoint accepts. Only one, for now. */
export const SSO_GRANT_TYPES = ['authorization_code'] as const;
export type SsoGrantType = (typeof SSO_GRANT_TYPES)[number];

/** PKCE methods. `S256` is required; `plain` is accepted only to reject it clearly. */
export const SSO_CODE_CHALLENGE_METHODS = ['S256', 'plain'] as const;
export type SsoCodeChallengeMethod = (typeof SSO_CODE_CHALLENGE_METHODS)[number];
